'use client';
import { Project, ProjectStateStatusEnum, Subproject, SubprojectStateStatusEnum } from '@/app/api';
import { createContext, ReactNode, useContext } from 'react';

const ProjectEndStates: ProjectStateStatusEnum[] = [
  ProjectStateStatusEnum.Cancelled,
  ProjectStateStatusEnum.Finished,
  ProjectStateStatusEnum.Closed,
];

const SubprojectEndStates: SubprojectStateStatusEnum[] = [SubprojectStateStatusEnum.Finished];

type Values = {
  project: Project;
  subProject?: Subproject;
};

const ProjectContext = createContext<Values | undefined>(undefined);

export const ProjectProvider = ({ children, values }: { children: ReactNode; values: Values }) => {
  return <ProjectContext.Provider value={{ ...values }}>{children}</ProjectContext.Provider>;
};

// Custom hook to safely access context
export const useProjectData = (): Values => {
  const context = useContext(ProjectContext);
  if (!context) {
    throw new Error('useProject must be used within a ProjectProvider');
  }
  return context;
};

export const useIsProjectPublic = (): boolean => {
  const context = useContext(ProjectContext);
  if (!context) {
    throw new Error('useProject must be used within a ProjectProvider');
  }
  return context.project.state.statusIsVisibleOnWeb;
};

export const useIsProjectActive = (): boolean => {
  const data = useProjectData();

  if (data.subProject) {
    return data.subProject.state.status === ProjectStateStatusEnum.Active;
  }

  return data.project.state.status === ProjectStateStatusEnum.Active;
};

export const useIsProjectInEndState = (): boolean => {
  const data = useProjectData();

  if (data.subProject) {
    return SubprojectEndStates.includes(data.subProject.state.status);
  }

  return ProjectEndStates.includes(data.project.state.status);
};
